/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import {
  ListeQueryContext,
  ListeTable,
  ListeTableContainer,
  type ListeType,
} from '@mairistem/liste';
import { request } from '@mairistem/tools';
import type { ApiListResponse } from '@mairistem/administres-utils';

import type { Csp, Profession } from '@/types';
import { getCsp } from '@/routes';

import ProfessionListeTableRow from './ProfessionListeTableRow';

interface Props {
  /** Nombre de lignes */
  count?: number;
  /** Callback appelé lors du chargement de la liste */
  onLoad?: () => void;
  /** Callback appelé lors de la sélection d'une ligne */
  onSelect?: (qualification: Profession, qualifications: Profession[]) => void;
  /** Nombre d'éléments définis par page */
  perPage?: number;
  /** Callback appelé lors d'un clique sur les filtres */
  onSort?: () => void;
  /** Données de la liste */
  items?: Profession[];
  /** État du chargement */
  loading?: boolean;
  /** Callback appelé lors de la sauvegarde (liste en saisie) */
  onSave?: (item: Profession) => void;
  /** Diplôme sélectionné */
  selectedProfession?: Profession;
  /** Callback appelé lors du reset des items */
  resetSelectedItems?: (item: Profession) => void;
  /** Booléen pour gérer l'affichage des professions caduques */
  isShowingCaduque?: boolean;
}

const ProfessionListeTable = (props: Props) => {
  const {
    count = 0,
    perPage = 50,
    items = [],
    selectedProfession = {},
    loading = false,
    onLoad = null,
    onSelect = null,
    onSort = null,
    onSave = null,
    resetSelectedItems = null,
    isShowingCaduque = false,
  } = props;

  const [errors, setErrors] = React.useState([]);
  const [csp, setCsp] = React.useState<Csp[]>([]);
  const [loadingCsp, setLoadingCsp] = React.useState<boolean>(false);

  const { addItem, removeItem, updateItem } =
    React.useContext(ListeQueryContext);

  const deleteProfessions = React.useCallback(
    (item) => {
      request
        .delete(`/api/transverse/professions/${item.identifiant}`)
        .then(() => {
          removeItem(item);
        });
    },
    [removeItem],
  );

  const postProfessions = React.useCallback(
    (item) => {
      request.post('/api/transverse/professions', item).then((i: ListeType) => {
        addItem(i);
      });
    },
    [addItem],
  );

  const putProfessions = React.useCallback(
    (item) => {
      request
        .put(`/api/transverse/professions/${item.identifiant}`, item)
        .then((i: ListeType) => {
          updateItem(i);
        });
    },
    [updateItem],
  );

  const handleDoubleClick = React.useCallback(
    (profession: Profession) => {
      if (onSelect) onSelect(profession, items);
      if (onSave) onSave(profession);
    },
    [items, onSave, onSelect],
  );

  const handleSaveProfession = React.useCallback(
    async (item: Profession, status: string) => {
      if (_.isEmpty(item?.libelle) || _.isEmpty(item?.code)) {
        setErrors(
          _.filter(
            [
              _.isEmpty(item?.libelle)
                ? {
                    field: 'libelle',
                    itemId: item?.identifiant,
                    message: 'Le libelle ne doit pas être vide',
                  }
                : null,
              _.isEmpty(item?.code)
                ? {
                    field: 'code',
                    itemId: item?.identifiant,
                    message: 'Le code ne doit pas être vide',
                  }
                : null,
            ],
            (profession) => !_.isNil(profession),
          ),
        );

        return false;
      }

      setErrors(null);

      if (status === 'creating') {
        try {
          await postProfessions(item);
          return true;
        } catch {
          setErrors([
            {
              itemId: item?.identifiant,
              message: 'Les données doivent être uniques',
            },
          ]);
          return false;
        }
      } else {
        if (item?.identifiant === selectedProfession?.identifiant) {
          onSave(item);
          resetSelectedItems(item);
        }
        return putProfessions(item);
      }
    },
    [
      onSave,
      selectedProfession,
      postProfessions,
      putProfessions,
      resetSelectedItems,
    ],
  );

  const handleAddProfession = React.useCallback(
    (newItem) => ({
      ...newItem,
      libelle: '',
      code: '',
      identifiantDomaine: 1,
    }),
    [],
  );

  const handleDeleteProfession = React.useCallback(
    (item) => {
      if (_.isEqual(item, selectedProfession)) {
        onSave({});
        resetSelectedItems({});
      }

      deleteProfessions(item);
    },
    [selectedProfession, deleteProfessions, onSave, resetSelectedItems],
  );

  const handleCancelClick = React.useCallback(() => {
    setErrors([]);
  }, []);

  React.useEffect(() => {
    setLoadingCsp(true);

    request
      .get(getCsp())
      .then((res: ApiListResponse<Csp>) => {
        setCsp(res?.data);
      })
      .finally(() => {
        setLoadingCsp(false);
      });
  }, []);

  const renderRow = (
    profession: Profession,
    _invalidate: any,
    rowProps: any,
    utilities: any,
  ) => (
    <ProfessionListeTableRow
      profession={profession}
      rowProps={rowProps}
      utilities={utilities}
      errors={errors}
      csp={csp}
      isShowingCaduque={isShowingCaduque}
      onDoubleClick={handleDoubleClick}
    />
  );

  // Forced to have 2 render functions, otherwise an error occurs
  const renderHeaderWithCaduque = () => (
    <ListeTable.Header>
      <ListeTable.Column
        name="code"
        sortable
        width={2}
        only={['m', 'l', 'xl']}
        required
      >
        Code
      </ListeTable.Column>
      <ListeTable.Column
        name="libelle"
        sortable
        width={7}
        only={['s', 'm', 'l', 'xl']}
        required
      >
        Libellé
      </ListeTable.Column>
      <ListeTable.Column name="csp" width={5} only={['m', 'l', 'xl']}>
        Code Sociaux Professionnels
      </ListeTable.Column>
      <ListeTable.Column
        key="caduque"
        name="caduque"
        width={2}
        only={['m', 'l', 'xl']}
      >
        Caduque
      </ListeTable.Column>
    </ListeTable.Header>
  );

  const renderHeader = () => (
    <ListeTable.Header>
      <ListeTable.Column
        name="code"
        sortable
        width={2}
        only={['m', 'l', 'xl']}
        required
      >
        Code
      </ListeTable.Column>
      <ListeTable.Column
        name="libelle"
        sortable
        width={9}
        only={['s', 'm', 'l', 'xl']}
        required
      >
        Libellé
      </ListeTable.Column>
      <ListeTable.Column name="csp" width={5} only={['m', 'l', 'xl']}>
        Code Sociaux Professionnels
      </ListeTable.Column>
    </ListeTable.Header>
  );

  return (
    <ListeTableContainer
      onAddClick={handleAddProfession}
      onValidateClick={handleSaveProfession}
      onCancelClick={handleCancelClick}
      onDeleteClick={handleDeleteProfession}
      additionLabel="Ajouter une nouvelle profession"
      items={items}
      errors={errors}
      switchToCreationIfEmpty
      allowAdditions
      allowModifications
      allowDeletions
    >
      <ListeTable
        count={count}
        loading={loading || loadingCsp}
        onLoad={onLoad}
        onSelect={onSelect}
        perPage={perPage}
        onSort={onSort}
        showCount
        countLabel={count > 1 ? 'professions' : 'profession'}
        selection="click"
        selectedItems={
          _.isEmpty(selectedProfession) ? [] : [selectedProfession]
        }
      >
        {isShowingCaduque ? renderHeaderWithCaduque() : renderHeader()}
        {renderRow as unknown as React.ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default ProfessionListeTable;
